import { create, getAll, remove, updateById } from '@/services/api';
import { filterAutomaticWelcomeMessages } from '@/utils/infrastructure/communication';
import { CoreEnums } from '@/utils/domain/constants';

// Small helper to wait between retries (to mitigate eventual consistency on Azure Hyperscale)
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const { ChatControlMode, ChatHandlerType } = CoreEnums;

const initialState = {
  chatThread: {},
  messages: [],
  threads: [],
  tasks: [],
  taskRuns: [],
  emailTracking: [],
  emailTrackingCount: 0,
  totalCount: 0, // For messages
  totalThreads: 0, // For threads (separate field)
  tasksCount: 0,
  taskRunsCount: 0,
  page: 1,
  pageSize: 10,
  isOpen: false,
  loadingMore: false,
  messageBuffer: {}, // Buffer for messages by threadId
  // Multi-agent support
  agents: [],
  selectedAgent: null,
  loadingAgents: false,
  agentsPage: 1,
  agentsPageSize: 5,
  totalAgents: 0,
  loadingMoreAgents: false,
  messagesThreadId: null,
  // Thread participants
  threadParticipants: {},
  participantsTotalCount: {},
  loadingParticipants: false,
  hasAllowedAgents: null,
};

const getMessageUniqueKey = (message) => {
  if (!message) return null;
  if (message.messageId) return String(message.messageId).toLowerCase();
  if (message.id) return String(message.id).toLowerCase();
  if (message.channelMessageIdentifier)
    return String(message.channelMessageIdentifier).toLowerCase();
  if (message.content?.messageId) return String(message.content.messageId).toLowerCase();
  if (message.content?.id) return String(message.content.id).toLowerCase();
  return null;
};

const pickLatestIsoTimestamp = (first, second) => {
  const normalizeIso = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value.toISOString();
    if (typeof value === 'string') {
      if (value.includes('Z') || value.includes('+')) return value;
      return `${value}Z`;
    }
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();
    return null;
  };

  const getMs = (value) => {
    if (!value) return 0;
    const normalized = normalizeIso(value);
    if (!normalized) return 0;
    const parsed = Date.parse(normalized);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const firstIso = normalizeIso(first);
  const secondIso = normalizeIso(second);

  const firstMs = getMs(firstIso);
  const secondMs = getMs(secondIso);

  return firstMs >= secondMs ? firstIso || secondIso : secondIso || firstIso;
};

const normalizeIsoForThread = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'string') {
    if (value.includes('Z') || value.includes('+')) return value;
    return `${value}Z`;
  }
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();
  return null;
};

const getTimestampMs = (value) => {
  if (!value) return 0;
  const normalized = normalizeIsoForThread(value);
  if (!normalized) return 0;
  const parsed = Date.parse(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const withThreadTimestamps = (thread, fallbackUpdated) => {
  if (!thread) return thread;
  const candidate = pickLatestIsoTimestamp(thread?.updated || thread?.created, fallbackUpdated);
  const updated =
    candidate ||
    normalizeIsoForThread(thread?.updated) ||
    normalizeIsoForThread(thread?.created) ||
    fallbackUpdated ||
    null;
  const updatedMs = getTimestampMs(
    updated || thread?.updated || thread?.created || fallbackUpdated,
  );

  return {
    ...thread,
    updated,
    updatedMs,
  };
};

const ChatModel = {
  namespace: 'chat',
  state: initialState,
  effects: {
    *resetChatState({ payload }, { put }) {
      try {
        yield put({ type: 'resetState' });
        if (payload?.onSuccess) payload.onSuccess();
      } catch (e) {
        if (payload?.onError) payload.onError(e);
      }
    },
    *fetchAgents({ payload }, { call, put, select }) {
      try {
        const isLoadingMore = payload?.loadMore;
        const { agentsPage, agentsPageSize } = yield select((state) => state.chat);

        if (isLoadingMore) {
          yield put({ type: 'setLoadingMoreAgents', payload: true });
        } else {
          yield put({ type: 'setLoadingAgents', payload: true });
        }

        const page = isLoadingMore ? agentsPage + 1 : 1;
        const skip = (page - 1) * agentsPageSize;

        const route = `ai/agent/allowed?$skip=${skip}&$top=${agentsPageSize}&$select=id,name,isActive,description,iconUrl,provider`;
        const res = yield call(getAll, { route });

        if (isLoadingMore) {
          yield put({ type: 'appendAgents', payload: { ...res, page } });
          yield put({ type: 'setLoadingMoreAgents', payload: false });
        } else {
          yield put({ type: 'saveAgents', payload: { ...res, page } });
          yield put({ type: 'setLoadingAgents', payload: false });
        }

        if (payload?.onSuccess) payload.onSuccess(res);
      } catch (e) {
        yield put({ type: 'setLoadingAgents', payload: false });
        yield put({ type: 'setLoadingMoreAgents', payload: false });
        if (payload?.onError) payload.onError(e);
      }
    },
    *fetchSpecificAgent({ payload }, { call, put, select }) {
      try {
        const { agentId } = payload;
        if (!agentId) return;

        const { agents } = yield select((state) => state.chat);

        // Check if agent is already loaded
        const existingAgent = agents.find((a) => a.id === agentId);
        if (existingAgent) {
          yield put({ type: 'setSelectedAgent', payload: existingAgent });
          if (payload?.onSuccess) payload.onSuccess(existingAgent);
          return;
        }

        // Load specific agent using OData filter
        const route = `ai/agent/allowed?$filter=id eq ${agentId}&$top=1&$select=id,name,isActive,description,iconUrl,provider`;
        const res = yield call(getAll, { route });

        if (res?.items?.length > 0) {
          const targetAgent = res.items[0];

          // Add agent to the list if not already present
          const updatedAgents = agents.find((a) => a.id === targetAgent.id)
            ? agents
            : [targetAgent, ...agents];

          yield put({
            type: 'updateAgentsList',
            payload: {
              agents: updatedAgents,
              totalAgents: Math.max(res.totalCount || 0, updatedAgents.length),
            },
          });

          yield put({ type: 'setSelectedAgent', payload: targetAgent });

          if (payload?.onSuccess) payload.onSuccess(targetAgent);
        } else {
          if (payload?.onError) payload.onError(new Error('Agent not found'));
        }
      } catch (e) {
        if (payload?.onError) payload.onError(e);
      }
    },
    *checkAllowedAgents({ payload }, { call, put, select }) {
      try {
        const storedContext = localStorage.getItem('x-userContext');
        let agentCount = 0;
        let hasStoredAgentCount = false;

        if (storedContext) {
          try {
            const parsed = JSON.parse(storedContext);
            if ('agentCount' in parsed) {
              agentCount = parsed.agentCount;
              hasStoredAgentCount = true;
            }
          } catch (e) {}
        }

        if (!hasStoredAgentCount) {
          yield put({ type: 'setLoadingAgents', payload: true });

          const { agentsPageSize } = yield select((state) => state.chat);
          const route = `ai/agent/allowed?$skip=0&$top=${agentsPageSize}&$select=id,name,isActive,description,iconUrl,provider`;
          const res = yield call(getAll, { route });

          agentCount = res?.totalCount || res?.items?.length || 0;

          const existingContext = storedContext ? JSON.parse(storedContext) : {};
          localStorage.setItem('x-userContext', JSON.stringify({ ...existingContext, agentCount }));

          if (agentCount > 0) {
            yield put({ type: 'saveAgents', payload: { ...res, page: 1 } });
          }

          yield put({ type: 'setLoadingAgents', payload: false });
        }

        const hasAgents = agentCount > 0;
        yield put({ type: 'setHasAllowedAgents', payload: hasAgents });

        if (payload?.onSuccess) payload.onSuccess(hasAgents);
      } catch (e) {
        yield put({ type: 'setLoadingAgents', payload: false });
        yield put({ type: 'setHasAllowedAgents', payload: false });
        if (payload?.onError) payload.onError(e);
      }
    },
    *fetchCurrentChat({ payload }, { call, put, select }) {
      try {
        const res = yield call(getAll, payload);
        yield put({ type: 'saveCurrentChat', payload: res });

        // If response includes agentId, update selected agent
        if (res.agentId) {
          const { agents } = yield select((state) => state.chat);
          const agent = agents.find((a) => a.id === res.agentId);

          if (agent) {
            yield put({ type: 'setSelectedAgent', payload: agent });
          } else {
            // Agent not found in current list, fetch it specifically
            yield put({
              type: 'fetchSpecificAgent',
              payload: { agentId: res.agentId },
            });
          }
        }

        if (payload.onSuccess) payload.onSuccess(res);
      } catch (e) {}
    },
    *fetchThreads({ payload }, { call, put }) {
      try {
        const {
          agentId = null,
          page = 1,
          pageSize = 10,
          orderBy = 'updated desc',
          route: customRoute,
        } = payload || {};
        const skip = (page - 1) * pageSize;
        const route =
          customRoute ||
          (agentId
            ? `chat/thread/paged/${agentId}?$skip=${skip}&$top=${pageSize}&$orderBy=${orderBy}`
            : `chat/thread/paged?$skip=${skip}&$top=${pageSize}&$orderBy=${orderBy}`);
        const res = yield call(getAll, { route });
        yield put({ type: 'saveThreads', payload: res });
      } catch (e) {}
    },
    *fetchTasks({ payload }, { call, put }) {
      try {
        // Build route in model to keep components free of business logic
        const { chatThreadId, agentId, page = 1, pageSize = 10, orderBy = 'updated desc' } =
          payload || {};
        const skip = (page - 1) * pageSize;
        const encodedOrderBy = encodeURIComponent(orderBy);
        const operationFilter = encodeURIComponent(
          "operationType eq Automy.Core.Enums.Chat.ChatInternalTaskOperationType'Task'",
        );
        let route;
        if (chatThreadId) {
          const filter = encodeURIComponent(
            `chatThreadId eq ${chatThreadId} and operationType eq Automy.Core.Enums.Chat.ChatInternalTaskOperationType'Task'`,
          );
          route = `chat/task/paged?$filter=${filter}&$skip=${skip}&$top=${pageSize}&$orderBy=${encodedOrderBy}`;
        } else if (payload?.route) {
          route = payload.route; // fallback for backward compatibility
        } else if (agentId) {
          const baseRoute = `chat/task/paged/${agentId}`;
          route = `${baseRoute}?$filter=${operationFilter}&$skip=${skip}&$top=${pageSize}&$orderBy=${encodedOrderBy}`;
        } else {
          return;
        }

        const res = yield call(getAll, { route });
        yield put({ type: 'saveTasks', payload: res });
      } catch (e) {}
    },
    *fetchThreadById({ payload }, { call, put }) {
      try {
        const { threadId, expectedStatus, optimisticUpdated, onSuccess, onError } = payload || {};
        if (!threadId) return;

        const threadRes = yield call(getAll, { route: `chat/thread/${threadId}` });

        const normalizedUpdated =
          pickLatestIsoTimestamp(threadRes?.updated, optimisticUpdated) ||
          threadRes?.created ||
          new Date().toISOString();

        const mergedThread = withThreadTimestamps(
          {
            ...threadRes,
            status:
              expectedStatus !== undefined && expectedStatus !== null
                ? expectedStatus
                : threadRes.status,
            updated: normalizedUpdated,
          },
          optimisticUpdated || threadRes?.updated || threadRes?.created,
        );

        yield put({ type: 'upsertThread', payload: mergedThread });
        if (onSuccess) onSuccess(mergedThread);
      } catch (error) {
        if (payload?.onError) payload.onError(error);
      }
    },
    *executeTask({ payload }, { call, put }) {
      try {
        yield call(create, {
          route: `chat/task/execute/${payload.id}`,
          method: 'POST',
        });
        if (payload.onSuccess) payload.onSuccess();
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },
    *toggleTaskStatus({ payload }, { call, put }) {
      try {
        const action = payload.status === 1 ? 'pause' : 'resume';
        yield call(create, {
          route: `chat/task/${action}/${payload.id}`,
          method: 'POST',
        });
        if (payload.onSuccess) payload.onSuccess();
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },
    *deleteTask({ payload }, { call, put }) {
      try {
        yield call(remove, {
          route: `chat/task`,
          id: payload.id,
        });
        if (payload.onSuccess) payload.onSuccess();
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },
    *fetchTaskRuns({ payload }, { call, put }) {
      try {
        const { taskId, page = 1, pageSize = 10, orderBy = 'created desc' } = payload || {};
        const skip = (page - 1) * pageSize;
        const route = `chat/task/runs/${taskId}/paged?$skip=${skip}&$top=${pageSize}&$orderBy=${orderBy}`;
        const res = yield call(getAll, { route });
        yield put({ type: 'saveTaskRuns', payload: res });
        if (payload?.onSuccess) payload.onSuccess();
      } catch (e) {
        if (payload?.onError) payload.onError(e);
      }
    },
    *fetchEmailTracking({ payload }, { call, put }) {
      try {
        const { threadId, onSuccess, onError } = payload || {};
        if (!threadId) return;

        const route = `chat/emailTracking/paged/${threadId}`;
        const res = yield call(getAll, { route });
        yield put({ type: 'saveEmailTracking', payload: res });
        if (onSuccess) onSuccess(res);
      } catch (e) {
        if (payload?.onError) payload.onError(e);
      }
    },
    *fetchRunDetails({ payload }, { call, put }) {
      try {
        const { taskId, runId } = payload || {};
        const route = `chat/task/runs/${taskId}/${runId}`;
        const res = yield call(getAll, { route });
        if (payload?.onSuccess) payload.onSuccess(res);
      } catch (e) {
        if (payload?.onError) payload.onError(e);
      }
    },
    *fetchMessages({ payload }, { call, put, select }) {
      try {
        // Build route in model
        const { threadId: threadIdParam, page = 1, pageSize = 10, orderBy = 'created desc' } =
          payload || {};
        const skip = (page - 1) * pageSize;
        const threadId =
          threadIdParam || (payload.route && payload.route.split('/')[2].split('?')[0]);
        const route = threadId
          ? `chat/messages/${threadId}?$skip=${skip}&$top=${pageSize}&$orderBy=${encodeURIComponent(
              orderBy,
            )}&$filter=messageType ne Automy.Core.Enums.Chat.ChatMessageType'UserJoinChat'&$select=id,chatThreadId,messageType,status,handlerType,senderType,userId,content,userName,errorMessage,created,updated`
          : payload.route;

        const res = yield call(getAll, { route });
        // Get current thread
        const { threads } = yield select((state) => state.chat);
        let currentThread = threads.find((t) => t.id === threadId);
        // If not found in threads, try to get it from response if res.thread exists
        if (!currentThread && res.thread) {
          currentThread = res.thread;
        }
        // Pass threadId explicitly to reducers to avoid ambiguity
        yield put({
          type: 'saveMessages',
          payload: { ...res, threadId },
        });
        if (currentThread) {
          yield put({
            type: 'saveCurrentChat',
            payload: currentThread,
          });
        }
      } catch (e) {}
    },
    *fetchMoreMessages({ payload }, { call, put, select }) {
      try {
        yield put({ type: 'setLoadingMore', payload: true });

        // Build route robustly to avoid /undefined when payload.route is absent
        const { page = 1, pageSize = 10, orderBy = 'created desc', threadId: payloadThreadId } =
          payload || {};

        // Determine threadId from payload or current state
        const state = yield select((s) => s.chat);
        const threadId = payloadThreadId || state.chatThread?.id || state.messagesThreadId;

        let route = payload?.route;
        if (!route) {
          if (!threadId) {
            // No route and no threadId -> nothing to load
            yield put({ type: 'setLoadingMore', payload: false });
            return;
          }
          const skip = (page - 1) * pageSize;
          route = `chat/messages/${threadId}?$skip=${skip}&$top=${pageSize}&$orderBy=${encodeURIComponent(
            orderBy,
          )}&$filter=messageType ne Automy.Core.Enums.Chat.ChatMessageType'UserJoinChat'&$select=id,chatThreadId,messageType,status,handlerType,senderType,userId,content,userName,errorMessage,created,updated`;
        }

        const res = yield call(getAll, { route });

        // Ensure pagination fields propagate to reducer
        const responseWithPaging = {
          ...res,
          page,
          pageSize,
        };
        yield put({ type: 'appendMessages', payload: responseWithPaging });
        yield put({ type: 'setLoadingMore', payload: false });
      } catch (e) {
        yield put({ type: 'setLoadingMore', payload: false });
      }
    },
    *queryUpdateMessages({ payload }, { put }) {
      yield put({ type: 'updateMessages', payload });
    },
    *fetchCloseChat({ payload }, { call, put, select }) {
      try {
        const res = yield call(create, payload);
        yield put({ type: 'saveCurrentChat', payload: {} });
        if (payload.onSuccess) payload.onSuccess();
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },
    *fetchOpenChat({ payload }, { call, put }) {
      yield put({ type: 'queryOpenChat', payload });
    },
    *changeThreadTitle({ payload }, { call, put }) {
      try {
        yield call(create, {
          route: `chat/thread/changeTitle/${payload.id}/${encodeURIComponent(payload.title)}`,
          method: 'POST',
        });
        if (payload.onSuccess) payload.onSuccess();
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },
    *deleteThread({ payload }, { call, put }) {
      try {
        yield call(remove, {
          route: `chat/thread`,
          id: payload.id,
        });
        if (payload.onSuccess) payload.onSuccess();
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },
    *fetchReopenChat({ payload }, { call, put }) {
      try {
        yield call(create, { route: payload.route, method: 'POST' });

        // Extract threadId from route (chat/openChat/threadId)
        const threadId = payload.route.split('/').pop();

        // After reopening, get the updated thread data to have the correct status
        const threadRes = yield call(getAll, { route: `chat/thread/${threadId}` });

        // Update the current thread with the new status
        yield put({ type: 'saveCurrentChat', payload: threadRes });

        // Update the thread in the threads list as well
        yield put({ type: 'updateThreadStatus', payload: { threadId, status: threadRes.status } });

        if (payload.onSuccess) payload.onSuccess();
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },
    *fetchNewChat({ payload }, { call, put }) {
      try {
        yield call(create, { route: payload.route, method: 'POST' });
        if (payload.onSuccess) payload.onSuccess();
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },
    *changeAgent({ payload }, { call, put, select }) {
      try {
        const { agentId, chatType } = payload;
        const route = agentId ? `chat/current/${chatType}/${agentId}` : `chat/current/${chatType}`;

        const res = yield call(getAll, { route });
        yield put({ type: 'saveCurrentChat', payload: res });

        // Ensure the correct agent is selected after changing
        if (agentId) {
          const { agents } = yield select((state) => state.chat);
          const agent = agents.find((a) => a.id === agentId);

          if (agent) {
            yield put({ type: 'setSelectedAgent', payload: agent });
          } else {
            // Agent not found in current list, fetch it specifically
            yield put({
              type: 'fetchSpecificAgent',
              payload: { agentId: agentId },
            });
          }
        }

        if (payload.onSuccess) payload.onSuccess(res);
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },
    *openSpecificChat({ payload }, { call, put, select }) {
      try {
        const { threadId, agentId } = payload;

        // First, get specific thread data
        const threadRes = yield call(getAll, { route: `chat/thread/${threadId}` });

        // If agentId is provided, update selected agent
        if (agentId || threadRes.agentId) {
          const finalAgentId = agentId || threadRes.agentId;
          const { agents } = yield select((state) => state.chat);
          let targetAgent = agents.find((a) => a.id === finalAgentId);

          // If no agents loaded, load first using correct route
          if (agents.length === 0) {
            const agentsRes = yield call(getAll, {
              route: 'ai/agent/allowed?$select=id,name,isActive,description,iconUrl,provider',
            });
            yield put({ type: 'saveAgents', payload: agentsRes });
            const updatedAgents = agentsRes.items || agentsRes || [];
            targetAgent = updatedAgents.find((a) => a.id === finalAgentId);
          }

          if (targetAgent) {
            yield put({ type: 'setSelectedAgent', payload: targetAgent });
          } else {
            // Agent not found in current list, fetch it specifically
            yield put({
              type: 'fetchSpecificAgent',
              payload: { agentId: finalAgentId },
            });
          }
        }

        // FIRST: Set current thread BEFORE opening chat
        // This prevents SupportChat from automatically fetching current chat
        yield put({ type: 'saveCurrentChat', payload: threadRes });

        // SECOND: Open chat - now SupportChat sees there's an active thread
        yield put({ type: 'setIsOpen', payload: true });

        // THIRD: Small delay to ensure SupportChat is fully initialized
        yield new Promise((resolve) => setTimeout(resolve, 200));

        // FOURTH: Dispatch event to force joinChat from component (twice to avoid race)
        // TODO: Refactor this DOM event-based communication to use React Context or a more elegant pattern
        window.dispatchEvent(
          new CustomEvent('forceJoinAutomyChat', {
            detail: { threadId },
          }),
        );
        // Redundant dispatch after a short delay to reduce timing flakiness
        setTimeout(() => {
          try {
            window.dispatchEvent(
              new CustomEvent('forceJoinAutomyChat', {
                detail: { threadId },
              }),
            );
          } catch (e) {}
        }, 150);

        // FIFTH: Load thread messages
        yield put({
          type: 'fetchMessages',
          payload: {
            route: `chat/messages/${threadId}?$skip=0&$top=10&$orderBy=created%20desc&$filter=messageType ne Automy.Core.Enums.Chat.ChatMessageType'UserJoinChat'&$select=id,chatThreadId,messageType,status,handlerType,senderType,userId,content,userName,errorMessage,created,updated`,
          },
        });

        // Call success callback if exists
        if (payload.onSuccess) payload.onSuccess({ threadId, agentId: threadRes.agentId });
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },

    // Thread Participants Management
    *fetchThreadParticipants({ payload }, { call, put }) {
      try {
        const { chatThreadId, page = 1, pageSize = 10 } = payload;
        const skip = (page - 1) * pageSize;
        const route = `chat/thread/participants/paged/${chatThreadId}?$skip=${skip}&$top=${pageSize}`;

        const response = yield call(getAll, { route });
        const responseItems =
          response?.items ||
          response?.data?.items ||
          response?.data?.data?.items ||
          (Array.isArray(response) ? response : []) ||
          [];
        const participants = Array.isArray(responseItems)
          ? responseItems
          : responseItems?.items || [];
        const totalCount =
          response?.totalCount ||
          response?.data?.totalCount ||
          response?.data?.data?.totalCount ||
          response?.count ||
          participants.length;

        yield put({
          type: 'saveThreadParticipants',
          payload: {
            chatThreadId,
            participants,
            totalCount,
          },
        });

        if (payload.onSuccess) payload.onSuccess({ items: participants, totalCount });
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },

    *addThreadParticipant({ payload }, { call, put }) {
      try {
        const { chatThreadId, userId, users, accessMode, onSuccess, onError } = payload;
        // Support both a single id (legacy) and an array of ids (new API)
        const usersArray = Array.isArray(users) ? users.filter(Boolean) : userId ? [userId] : [];

        if (!chatThreadId || !accessMode || usersArray.length === 0) {
          if (onError) onError(new Error('Invalid payload for adding participants'));
          return;
        }

        const route = `chat/thread/participants/add/${chatThreadId}/${accessMode}`;

        const response = yield call(create, {
          route,
          method: 'POST',
          data: { users: usersArray },
        });

        // Single delayed refresh to reconcile (2s)
        yield call(sleep, 2000);
        yield put({ type: 'fetchThreadParticipants', payload: { chatThreadId } });

        if (onSuccess) onSuccess(response);
      } catch (e) {
        if (payload?.onError) payload.onError(e);
      }
    },

    *updateThreadParticipant({ payload }, { call, put, select }) {
      try {
        const { participantId, newAccessMode, chatThreadId } = payload;
        const route = `chat/thread/participants/change/${participantId}/${newAccessMode}`;

        // Use direct request with full route to avoid duplicating the participantId in the URL
        const response = yield call(create, {
          route,
          method: 'PATCH',
        });

        // Conditional polling: stop early as soon as backend reflects the change
        if (chatThreadId) {
          const delays = [0, 200, 600, 1200];
          for (let i = 0; i < delays.length; i += 1) {
            const wait = delays[i];
            if (wait > 0) {
              yield call(sleep, wait);
            }
            // Direct read to ensure we check latest backend state
            const pollRoute = `chat/thread/participants/paged/${chatThreadId}?$skip=0&$top=10`;
            const pollResponse = yield call(getAll, { route: pollRoute });

            const items = pollResponse.items || [];
            const matched = items.some(
              (p) => p.id === participantId && Number(p.accessMode) === Number(newAccessMode),
            );

            // Only update the store when the backend reflects the new state
            if (matched || i === delays.length - 1) {
              yield put({
                type: 'saveThreadParticipants',
                payload: {
                  chatThreadId,
                  participants: items,
                  totalCount: pollResponse.totalCount || 0,
                },
              });
              if (matched) {
                break;
              }
            }
          }
        }

        if (payload.onSuccess) payload.onSuccess(response);
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },

    *removeThreadParticipant({ payload }, { call, put }) {
      try {
        const { participantId, chatThreadId } = payload;
        const route = `chat/thread/participants`;

        const response = yield call(remove, { id: participantId, route });

        // Do not refresh on success; UI already applied optimistic removal

        if (payload.onSuccess) payload.onSuccess(response);
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },
  },
  reducers: {
    saveCurrentChat(state, { payload }) {
      if (!payload) {
        return state;
      }

      const normalizedControlMode =
        payload.controlMode !== undefined && payload.controlMode !== null
          ? payload.controlMode
          : state.chatThread?.controlMode !== undefined && state.chatThread?.controlMode !== null
          ? state.chatThread.controlMode
          : ChatControlMode?.Auto || 1;

      return {
        ...state,
        chatThread: {
          ...payload,
          controlMode: normalizedControlMode,
        },
      };
    },
    saveThreads(state, { payload }) {
      const existingThreadsMap = (state.threads || []).reduce((acc, thread) => {
        if (thread?.id !== undefined && thread?.id !== null) {
          acc[thread.id.toString()] = thread;
        }
        return acc;
      }, {});

      const threadsArray = ((payload && payload.items) || []).map((thread) => {
        const stringId =
          thread?.id !== undefined && thread?.id !== null ? thread.id.toString() : null;
        const existing = stringId ? existingThreadsMap[stringId] : null;
        const normalizedControlMode =
          thread?.controlMode !== undefined && thread?.controlMode !== null
            ? thread.controlMode
            : existing?.controlMode !== undefined && existing?.controlMode !== null
            ? existing.controlMode
            : ChatControlMode?.Auto || 1;
        return withThreadTimestamps(
          {
            ...thread,
            controlMode: normalizedControlMode,
          },
          existing?.updated || existing?.created,
        );
      });

      const sortedThreads = threadsArray.sort((a, b) => {
        const dateA = a?.updatedMs ?? getTimestampMs(a?.updated || a?.created);
        const dateB = b?.updatedMs ?? getTimestampMs(b?.updated || b?.created);
        return dateB - dateA;
      });

      return {
        ...state,
        threads: sortedThreads,
        totalThreads: payload.totalCount || 0, // Use separate field for threads
        // totalCount is preserved for messages
      };
    },
    upsertThread(state, { payload }) {
      const threads = [...state.threads];
      const threadId = payload?.id ? String(payload.id) : null;
      const existingIndex = threadId ? threads.findIndex((t) => String(t.id) === threadId) : -1;
      const existingThread = existingIndex !== -1 ? threads[existingIndex] : null;

      const baseThread = existingThread ? { ...existingThread, ...payload } : payload;
      const mergedThread = withThreadTimestamps(
        baseThread,
        existingThread?.updated || existingThread?.created,
      );

      const safeUpdated = pickLatestIsoTimestamp(
        mergedThread.updated || mergedThread.created,
        existingThread?.updated || existingThread?.created,
      );
      mergedThread.updated = safeUpdated || new Date().toISOString();
      mergedThread.updatedMs = getTimestampMs(mergedThread.updated);

      if (existingIndex !== -1) {
        threads[existingIndex] = mergedThread;
      } else {
        threads.unshift(mergedThread);
      }

      threads.sort((a, b) => {
        const dateA = a?.updatedMs ?? getTimestampMs(a?.updated || a?.created);
        const dateB = b?.updatedMs ?? getTimestampMs(b?.updated || b?.created);
        return dateB - dateA;
      });

      const nextState = {
        ...state,
        threads,
      };

      return nextState;
    },
    saveTasks(state, { payload }) {
      const tasks =
        (Array.isArray(payload?.items) && payload.items) ||
        (Array.isArray(payload?.value) && payload.value) ||
        (Array.isArray(payload) ? payload : []) ||
        [];
      const tasksCount =
        payload?.totalCount ?? payload?.count ?? payload?.items?.length ?? tasks.length ?? 0;

      return {
        ...state,
        tasks,
        tasksCount,
      };
    },
    updateTaskStatus(state, { payload }) {
      const { taskId, status } = payload;

      // Update the task in the list of tasks if it exists
      const updatedTasks = state.tasks.map((task) => {
        if (task.id === taskId) {
          return {
            ...task,
            status: status,
            updated: new Date().toISOString(),
          };
        }
        return task;
      });

      return {
        ...state,
        tasks: updatedTasks,
      };
    },
    saveTaskRuns(state, { payload }) {
      return {
        ...state,
        taskRuns: payload.items || [],
        taskRunsCount: payload.totalCount || 0,
      };
    },
    saveEmailTracking(state, { payload }) {
      const emails =
        (Array.isArray(payload?.items) && payload.items) ||
        (Array.isArray(payload?.value) && payload.value) ||
        (Array.isArray(payload) ? payload : []) ||
        [];
      const emailTrackingCount =
        payload?.totalCount ?? payload?.count ?? payload?.items?.length ?? emails.length ?? 0;

      return {
        ...state,
        emailTracking: emails,
        emailTrackingCount,
      };
    },
    saveMessages(state, { payload }) {
      if (!payload || !payload.items) {
        return state;
      }

      const toMs = (iso) => {
        if (!iso) return 0;
        const s = String(iso);
        return (s.includes('Z') || s.includes('+') ? new Date(s) : new Date(`${s}Z`)).getTime();
      };

      // Extract threadId from response, route-provided payload.threadId, or first message
      const threadId =
        payload.threadId ||
        (payload.items[0] && (payload.items[0].chatThreadId || payload.items[0].threadId)) ||
        state.chatThread?.id;

      // Get messages from buffer for this threadId
      const bufferedMessages = state.messageBuffer[threadId] || [];

      // Filter out automatic welcome messages from the backend
      const filteredMessages = filterAutomaticWelcomeMessages(payload.items);

      // Combine filtered messages with buffer messages, avoiding duplicates
      const allMessagesRaw = [];
      const seenKeys = new Set();
      [...filteredMessages, ...bufferedMessages].forEach((msg, idx) => {
        const key = getMessageUniqueKey(msg) || `__idx_${idx}`;
        if (!seenKeys.has(key)) {
          seenKeys.add(key);
          allMessagesRaw.push(msg);
        }
      });

      // Normalize and cache createdMs for efficient sorting
      const allMessages = allMessagesRaw.map((m) => {
        if (m && typeof m.createdMs !== 'number') {
          return { ...m, createdMs: toMs(m.created) };
        }
        return m;
      });

      // Preserve totalCount if it's higher than the new value (infinite scroll protection)
      // This prevents FullscreenChat fetches from resetting the totalCount incorrectly
      const shouldPreserveTotalCount =
        state.totalCount > 0 &&
        payload.totalCount > 0 &&
        state.totalCount > payload.totalCount &&
        state.messagesThreadId === threadId; // Only preserve for same thread

      const finalMessages = allMessages.sort((a, b) => (b.createdMs || 0) - (a.createdMs || 0));

      return {
        ...state,
        messages: finalMessages,
        totalCount: shouldPreserveTotalCount ? state.totalCount : payload.totalCount,
        page: payload.page,
        pageSize: payload.pageSize,
        messagesThreadId: threadId,
        // Clear the buffer for this threadId
        messageBuffer: {
          ...state.messageBuffer,
          [threadId]: [],
        },
      };
    },
    appendMessages(state, { payload }) {
      if (!payload || !payload.items || payload.items.length === 0) {
        return state;
      }

      const toMs = (iso) => {
        if (!iso) return 0;
        const s = String(iso);
        return (s.includes('Z') || s.includes('+') ? new Date(s) : new Date(`${s}Z`)).getTime();
      };

      // Filter out automatic welcome messages from the backend
      const filteredNewMessages = filterAutomaticWelcomeMessages(payload.items);

      // Filter duplicate messages
      const newMessages = filteredNewMessages.filter((newMsg) => {
        const newKey = getMessageUniqueKey(newMsg);
        if (!newKey) {
          return !state.messages.some((existingMsg) => !getMessageUniqueKey(existingMsg));
        }
        return !state.messages.some((existingMsg) => getMessageUniqueKey(existingMsg) === newKey);
      });

      // Combine existing messages with new ones
      const combinedRaw = [...newMessages, ...state.messages];
      const combinedUnique = [];
      const seenCombined = new Set();
      combinedRaw.forEach((msg, idx) => {
        const key = getMessageUniqueKey(msg) || `__idx_${idx}`;
        if (!seenCombined.has(key)) {
          seenCombined.add(key);
          combinedUnique.push(msg);
        }
      });
      // Ensure all have createdMs for efficient sorting
      const combinedMessages = combinedUnique
        .map((m) => {
          if (m && typeof m.createdMs !== 'number') {
            return { ...m, createdMs: toMs(m.created) };
          }
          return m;
        })
        .sort((a, b) => (b.createdMs || 0) - (a.createdMs || 0));

      // Preserve totalCount if it's higher than the new value (infinite scroll protection)
      const shouldPreserveTotalCount =
        state.totalCount > 0 && payload.totalCount > 0 && state.totalCount > payload.totalCount;

      return {
        ...state,
        messages: combinedMessages,
        totalCount: shouldPreserveTotalCount ? state.totalCount : payload.totalCount,
        page: payload.page,
        pageSize: payload.pageSize,
      };
    },
    setLoadingMore(state, { payload }) {
      return {
        ...state,
        loadingMore: payload,
      };
    },
    clearMessages(state, { payload = {} }) {
      // Preserve buffer for current thread if specified
      const currentThreadId = payload.preserveBufferForThread || state.chatThread?.id;
      const preservedBuffer =
        currentThreadId && state.messageBuffer[currentThreadId]
          ? { [currentThreadId]: state.messageBuffer[currentThreadId] }
          : {};

      return {
        ...state,
        messages: [],
        totalCount: 0,
        page: 1,
        pageSize: 10,
        messagesThreadId: null, // Also clear messagesThreadId to avoid sync issues
        messageBuffer: preservedBuffer, // Preserve buffer for current thread only
      };
    },
    updateMessages(state, { payload }) {
      if (payload.messageType === 'response') {
        const thinkingIndex = state.messages.findIndex(
          (message) =>
            message.messageType === 'agent-typing' || message.messageType === 'ai-thinking',
        );

        if (thinkingIndex !== -1) {
          const newMessages = [...state.messages];
          newMessages[thinkingIndex] = payload;
          return {
            ...state,
            messages: newMessages,
          };
        }

        // Add new message at beginning to maintain descending order
        return {
          ...state,
          messages: [payload, ...state.messages],
        };
      }

      // Check if message with same ID already exists (avoid duplicates)
      const existingIndex = state.messages.findIndex((msg) => msg.id === payload.id);
      if (existingIndex !== -1) {
        // If it already exists, replace it
        const newMessages = [...state.messages];
        newMessages[existingIndex] = payload;
        return {
          ...state,
          messages: newMessages,
        };
      }

      // Add new message at beginning to maintain descending order
      return {
        ...state,
        messages: [payload, ...state.messages],
      };
    },
    queryOpenChat(state, { payload }) {
      return {
        ...state,
        isOpen: payload,
      };
    },
    // New reducer to handle socket messages
    receiveSocketMessage(state, { payload }) {
      const messageThreadId = payload.threadId;
      const currentThreadId = state.chatThread?.id;

      // CRITICAL FILTER: Only process messages from current thread
      if (
        !messageThreadId ||
        !currentThreadId ||
        messageThreadId.toString() !== currentThreadId.toString()
      ) {
        return state; // Don't modify state if not from current thread
      }

      // If threadId is active but messages haven't been loaded yet
      if (state.messagesThreadId !== currentThreadId) {
        // Buffer the message
        return {
          ...state,
          messageBuffer: {
            ...state.messageBuffer,
            [messageThreadId]: [...(state.messageBuffer[messageThreadId] || []), payload],
          },
        };
      }

      // Normalize the ID of the message (may come as 'id' or 'messageId')
      const normalizedId =
        payload.id ||
        payload.messageId ||
        `socket-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;

      const normalizeErrorArray = (collection) => {
        if (!Array.isArray(collection)) {
          return '';
        }
        return collection
          .map((entry) => {
            if (!entry) {
              return '';
            }
            if (typeof entry === 'string') {
              return entry;
            }
            if (typeof entry === 'object' && entry.message) {
              return entry.message;
            }
            return String(entry);
          })
          .filter((entry) => entry && entry.trim())
          .join('\n')
          .trim();
      };

      const resolveStatusValue = (...candidates) => {
        for (let i = 0; i < candidates.length; i += 1) {
          const candidate = candidates[i];
          if (candidate === undefined || candidate === null || candidate === '') {
            continue;
          }
          return candidate;
        }
        return null;
      };

      const normalizedContent = payload?.content ? { ...payload.content } : undefined;
      const resolvedStatus = resolveStatusValue(
        payload?.status,
        payload?.messageStatus,
        payload?.chatMessageStatus,
        payload?.data?.status,
        payload?.content?.status,
      );

      if (
        normalizedContent &&
        normalizedContent.status === undefined &&
        resolvedStatus !== null &&
        resolvedStatus !== undefined
      ) {
        normalizedContent.status = resolvedStatus;
      }

      const resolvedSocketErrorMessage =
        payload?.errorMessage ||
        payload?.data?.errorMessage ||
        payload?.content?.errorMessage ||
        payload?.error?.message ||
        payload?.error?.errorMessage ||
        payload?.content?.error?.message ||
        normalizeErrorArray(payload?.errorMessages) ||
        normalizeErrorArray(payload?.errors) ||
        normalizeErrorArray(payload?.content?.errorMessages) ||
        normalizeErrorArray(payload?.content?.errors) ||
        normalizeErrorArray(payload?.data?.errorMessages) ||
        normalizeErrorArray(payload?.data?.errors);

      const messageWithId = {
        ...payload,
        ...(normalizedContent ? { content: normalizedContent } : {}),
        ...(resolvedStatus !== null && resolvedStatus !== undefined
          ? {
              status: resolvedStatus,
              messageStatus:
                payload?.messageStatus !== undefined && payload?.messageStatus !== null
                  ? payload.messageStatus
                  : resolvedStatus,
              chatMessageStatus:
                payload?.chatMessageStatus !== undefined && payload?.chatMessageStatus !== null
                  ? payload.chatMessageStatus
                  : resolvedStatus,
            }
          : {}),
        ...(resolvedSocketErrorMessage ? { errorMessage: resolvedSocketErrorMessage } : {}),
        id: normalizedId,
        // Ensure created exists for proper ordering
        created: payload.created || new Date().toISOString(),
        // Map replyToChatMessageId from socket payload
        replyToChatMessageId: payload.replyToChatMessageId || null,
      };

      if (!messageWithId.messageId) {
        messageWithId.messageId =
          payload.messageId || payload.id || payload.channelMessageIdentifier || normalizedId;
      }

      const incomingKey = getMessageUniqueKey(messageWithId);
      if (incomingKey) {
        const existingIndex = state.messages.findIndex(
          (msg) => getMessageUniqueKey(msg) === incomingKey,
        );
        if (existingIndex !== -1) {
          const newMessages = [...state.messages];
          const existingMessage = newMessages[existingIndex] || {};

          const mergedContent =
            existingMessage.content || messageWithId.content
              ? {
                  ...(existingMessage.content || {}),
                  ...(messageWithId.content || {}),
                }
              : undefined;

          const mergedMessage = {
            ...existingMessage,
            ...messageWithId,
            ...(mergedContent ? { content: mergedContent } : {}),
            isOptimistic: false,
          };

          newMessages[existingIndex] = mergedMessage;
          return {
            ...state,
            messages: newMessages,
          };
        }
      }

      // If already loaded, check if there's an optimistic message to replace
      // Prefer matching by id/messageId, fallback to same userId
      if (
        messageWithId.messageType === 'user' ||
        messageWithId.messageType === 'user-message' ||
        messageWithId.messageType === 2 ||
        messageWithId.messageType === CoreEnums?.ChatMessageType?.UserMessage ||
        messageWithId.userId
      ) {
        const optimisticIndexById = state.messages.findIndex(
          (msg) =>
            msg.isOptimistic &&
            (msg.id === messageWithId.id ||
              msg.id === messageWithId.messageId ||
              msg.messageId === messageWithId.id),
        );

        let optimisticIndex = optimisticIndexById;
        if (optimisticIndex === -1) {
          optimisticIndex = state.messages.findIndex(
            (msg) => msg.isOptimistic && msg.userId === messageWithId.userId,
          );
        }

        if (optimisticIndex !== -1) {
          const newMessages = [...state.messages];
          newMessages[optimisticIndex] = { ...messageWithId, isOptimistic: false };
          return {
            ...state,
            messages: newMessages,
          };
        }
      }

      // If no optimistic message to replace, add and then keep ordered by created desc
      return {
        ...state,
        messages: [messageWithId, ...state.messages],
      };
    },
    // New reducer to update specific thread state
    updateThreadStatus(state, { payload }) {
      const { threadId, status, updated } = payload;

      // Update the thread in the list of threads if it exists
      const updatedThreads = state.threads.map((thread) => {
        if (thread.id.toString() === threadId.toString()) {
          return {
            ...thread,
            status: status,
            updated: updated || thread.updated,
          };
        }
        return thread;
      });

      // Also update the current chatThread if it is the same thread
      let updatedChatThread = state.chatThread;
      if (state.chatThread?.id?.toString() === threadId.toString()) {
        updatedChatThread = {
          ...state.chatThread,
          status: status,
          updated: updated || state.chatThread.updated,
        };
      }

      return {
        ...state,
        threads: updatedThreads,
        chatThread: updatedChatThread,
      };
    },
    updateThreadControlMode(state, { payload }) {
      const { threadId } = payload || {};
      if (!threadId) {
        return state;
      }

      const controlValue =
        payload?.controlMode !== undefined && payload?.controlMode !== null
          ? payload.controlMode
          : ChatControlMode?.Auto || 1;

      if (!threadId) {
        return state;
      }

      const stringId = threadId.toString();

      const updatedThreads = state.threads.map((thread) => {
        if (thread.id?.toString() === stringId) {
          return {
            ...thread,
            controlMode: controlValue,
          };
        }
        return thread;
      });

      let updatedChatThread = state.chatThread;
      if (state.chatThread?.id?.toString() === stringId) {
        updatedChatThread = {
          ...state.chatThread,
          controlMode: controlValue,
        };
      }

      return {
        ...state,
        threads: updatedThreads,
        chatThread: updatedChatThread,
      };
    },
    updateThreadHandlerType(state, { payload }) {
      const { threadId } = payload || {};
      if (!threadId) {
        return state;
      }

      const handlerValue =
        payload?.handlerType !== undefined && payload?.handlerType !== null
          ? payload.handlerType
          : ChatHandlerType?.AI || 2;

      const stringId = threadId.toString();

      const updatedThreads = state.threads.map((thread) => {
        if (thread.id?.toString() === stringId) {
          return {
            ...thread,
            handlerType: handlerValue,
          };
        }
        return thread;
      });

      let updatedChatThread = state.chatThread;
      if (state.chatThread?.id?.toString() === stringId) {
        updatedChatThread = {
          ...state.chatThread,
          handlerType: handlerValue,
        };
      }

      return {
        ...state,
        threads: updatedThreads,
        chatThread: updatedChatThread,
      };
    },
    // New reducer to add individual thread to store
    addThreadToStore(state, { payload }) {
      // Verify that thread doesn't already exist in the list
      const exists = state.threads.find((t) => t.id.toString() === payload.id.toString());
      if (exists) {
        return state; // Ya existe, no duplicar
      }

      const normalizedControlMode =
        payload.controlMode !== undefined && payload.controlMode !== null
          ? payload.controlMode
          : ChatControlMode?.Auto || 1;

      // Add to beginning of list
      return {
        ...state,
        threads: [{ ...payload, controlMode: normalizedControlMode }, ...state.threads],
        totalCount: state.totalCount + 1,
      };
    },
    // New reducer to remove individual thread from store
    removeThreadFromStore(state, { payload }) {
      const { threadId } = payload;

      // Filter the thread to remove it from the list
      const filteredThreads = state.threads.filter(
        (thread) => thread.id.toString() !== threadId.toString(),
      );

      return {
        ...state,
        threads: filteredThreads,
        totalCount: Math.max(0, state.totalCount - 1),
      };
    },
    // Multi-agent reducers
    saveAgents(state, { payload }) {
      const agentsArray = payload.items || payload || [];
      let defaultAgent = null;

      // Find default agent
      if (agentsArray.length > 0) {
        defaultAgent =
          agentsArray.find((agent) => agent.id === 'eb77a9b1-c65d-48dd-a76c-61257cc65505') ||
          agentsArray[0];
      }

      return {
        ...state,
        agents: agentsArray,
        selectedAgent: state.selectedAgent || defaultAgent,
        agentsPage: payload.page || 1,
        totalAgents: payload.totalCount || agentsArray.length,
      };
    },
    appendAgents(state, { payload }) {
      const newAgents = payload.items || payload || [];
      const existingAgents = state.agents || [];

      // Filter out duplicates
      const filteredNewAgents = newAgents.filter(
        (newAgent) => !existingAgents.some((existing) => existing.id === newAgent.id),
      );

      return {
        ...state,
        agents: [...existingAgents, ...filteredNewAgents],
        agentsPage: payload.page || state.agentsPage,
        totalAgents: payload.totalCount || state.totalAgents,
      };
    },
    setSelectedAgent(state, { payload }) {
      return {
        ...state,
        selectedAgent: payload,
      };
    },
    updateAgentsList(state, { payload }) {
      return {
        ...state,
        agents: payload.agents,
        totalAgents: payload.totalAgents,
      };
    },
    setLoadingAgents(state, { payload }) {
      return {
        ...state,
        loadingAgents: payload,
      };
    },
    setLoadingMoreAgents(state, { payload }) {
      return {
        ...state,
        loadingMoreAgents: payload,
      };
    },
    setHasAllowedAgents(state, { payload }) {
      return {
        ...state,
        hasAllowedAgents: payload,
      };
    },
    setIsOpen(state, { payload }) {
      return {
        ...state,
        isOpen: payload,
      };
    },
    resetState() {
      return initialState;
    },
    saveThreadParticipants(state, { payload }) {
      const { chatThreadId, participants, totalCount } = payload;
      return {
        ...state,
        threadParticipants: {
          ...state.threadParticipants,
          [chatThreadId]: participants,
        },
        participantsTotalCount: {
          ...state.participantsTotalCount,
          [chatThreadId]: totalCount,
        },
      };
    },
    optimisticUpdateParticipantAccess(state, { payload }) {
      const { chatThreadId, participantId, newAccessMode } = payload;
      const list = state.threadParticipants[chatThreadId] || [];
      const updatedList = list.map((p) =>
        p.id === participantId ? { ...p, accessMode: newAccessMode } : p,
      );
      return {
        ...state,
        threadParticipants: {
          ...state.threadParticipants,
          [chatThreadId]: updatedList,
        },
      };
    },
    optimisticAddParticipants(state, { payload }) {
      const { chatThreadId, users, accessMode } = payload;
      const current = state.threadParticipants[chatThreadId] || [];
      const toSafeText = (value, fallbackPrefix = 'Usuario') => {
        if (typeof value === 'string') return value;
        if (typeof value === 'number') return String(value);
        if (value && typeof value === 'object') {
          const candidates = [
            value.fullName,
            value.userName,
            value.name,
            value.email,
            value.title,
            value.value,
            value.label,
          ];
          const found = candidates.find((item) => typeof item === 'string' && item.trim());
          if (found) return found;
          if (value.id) return `${fallbackPrefix} ${value.id}`;
        }
        return `${fallbackPrefix}`.trim();
      };

      const newItems = (users || []).map((u) => {
        const userId = typeof u === 'object' ? u.id : u;
        const fullName =
          typeof u === 'object'
            ? toSafeText(
                u.fullName || u.userName || u.name || u.email || u.label || u.value,
                'Usuario',
              )
            : toSafeText(u, 'Usuario');
        const userName =
          typeof u === 'object'
            ? toSafeText(
                u.userName || u.fullName || u.name || u.email || u.label || u.value,
                'Usuario',
              )
            : toSafeText(u, 'Usuario');
        return {
          id: `temp-${userId}-${Date.now()}`,
          user: { id: userId, fullName, userName },
          userName: userName || fullName,
          accessMode,
          isOptimistic: true,
        };
      });
      return {
        ...state,
        threadParticipants: {
          ...state.threadParticipants,
          [chatThreadId]: [...newItems, ...current],
        },
        participantsTotalCount: {
          ...state.participantsTotalCount,
          [chatThreadId]:
            (state.participantsTotalCount[chatThreadId] || current.length) + newItems.length,
        },
      };
    },
    optimisticRemoveParticipant(state, { payload }) {
      const { chatThreadId, participantId } = payload;
      const list = state.threadParticipants[chatThreadId] || [];
      const updatedList = list.filter((p) => p.id !== participantId);
      return {
        ...state,
        threadParticipants: {
          ...state.threadParticipants,
          [chatThreadId]: updatedList,
        },
        participantsTotalCount: {
          ...state.participantsTotalCount,
          [chatThreadId]: Math.max(
            (state.participantsTotalCount[chatThreadId] || list.length) - 1,
            0,
          ),
        },
      };
    },
    setLoadingParticipants(state, { payload }) {
      return {
        ...state,
        loadingParticipants: payload,
      };
    },
  },
};

export default ChatModel;
